import { useState, useEffect, useRef } from "react";
import { useSnackbar } from "notistack";
import { Button } from "src/@/components/ui/button";
import { Input } from "src/@/components/ui/input";
import { Label } from "src/@/components/ui/label";
import { Dialog, DialogContent, DialogTitle } from "src/@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/@/components/ui/select";
import { ScrollArea } from "src/@/components/ui/scroll-area";
import Iconify from "src/components/Iconify";
import getActionPesquisarChat from "src/actions/chats/getActionPesquisarChat";
import getActionChatInfo from "src/actions/chats/getActionChatInfo";
import getActionCriarChatAndNovaMensagem from "src/actions/chats/getActionCriarChatAndNovaMensagem";
import actionListaClientesCanais from "src/actions/clientes/actionListaClientesCanais";
import NovoChatForm from "src/sections/@dashboard/cadastro/NovoChatForm";
import { formataNumeroCelular, validarNumeroWhatsapp } from "src/utils/functions";

const MenuChannelChat = ({ open, onClose, cliente, user, onSelectChat }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [listaContatos, setListaContatos] = useState([]);
  const [pesquisa, setPesquisa] = useState("");
  const [modalAlerta, setModalAlerta] = useState(false);
  const [modalAlertaMensagem, setModalAlertaMensagem] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [modoCadastro, setModoCadastro] = useState(false);
  const [loadingCriar, setLoadingCriar] = useState(false);
  const [listaClientesCanais, setListaClientesCanais] = useState([]);
  const [selectedClienteId, setSelectedClienteId] = useState(null);
  const timeoutRef = useRef(null);
  const pesquisaAtualRef = useRef("");

  const isMultisession = cliente?.multiSession === 1;

  // Limpar pesquisa quando o modal fechar
  useEffect(() => {
    if (!open) {
      setPesquisa("");
      setListaContatos([]);
      setModoCadastro(false);
    }
  }, [open]);

  // Carregar canais do cliente (necessário para escolher o canal em multisessão)
  useEffect(() => {
    async function buscarCanais() {
      if (!open || !cliente?._id) {
        return;
      }

      if (!isMultisession) {
        setSelectedClienteId(cliente._id);
        return;
      }

      try {
        const response = await actionListaClientesCanais(cliente._id);
        const canais = response?.data?.data || [];
        setListaClientesCanais(canais);
        setSelectedClienteId(canais[0]?.cliente_id || cliente._id);
      } catch (error) {
        console.error("Erro ao buscar canais:", error);
        setListaClientesCanais([]);
        setSelectedClienteId(cliente._id);
      }
    }

    buscarCanais();
  }, [open, cliente?._id, isMultisession]);

  // Limpar timeout quando componente desmontar
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const pesquisarChat = async (value) => {
    try {
      setIsLoading(true);
      pesquisaAtualRef.current = value;

      if (!cliente?._id) {
        setListaContatos([]);
        return;
      }

      const data = {
        clienteId: cliente._id,
        search: value,
      };

      const result = await getActionPesquisarChat(data);

      // Descartar resposta obsoleta: outra pesquisa mais recente foi disparada
      if (pesquisaAtualRef.current !== value) {
        return;
      }

      // Verificar diferentes estruturas possíveis da resposta
      let chats = [];
      if (result?.data?.chats) {
        chats = result.data.chats;
      } else if (result?.data?.data?.chats) {
        chats = result.data.data.chats;
      } else if (result?.data && Array.isArray(result.data)) {
        chats = result.data;
      }

      if (Array.isArray(chats) && chats.length > 0) {
        setListaContatos(chats);
      } else {
        setListaContatos([]);
      }
    } catch (error) {
      // Ignora erro de resposta obsoleta
      if (pesquisaAtualRef.current === value) {
        setListaContatos([]);
      }
    } finally {
      // Só encerra o loading se esta ainda for a pesquisa mais recente
      if (pesquisaAtualRef.current === value) {
        setIsLoading(false);
      }
    }
  };

  const validarInclusaoChatLead = (value) => {
    const { direciona_usuarios, statusRoom } = value;
    const statusPermitidos = ["Finalizado", "Avaliando", "Geral"];

    if (statusPermitidos.includes(statusRoom)) {
      onSelectChat(value);
    } else if (Array.isArray(direciona_usuarios) && direciona_usuarios.includes(user?.id)) {
      onSelectChat(value);
    } else {
      setModalAlerta(true);
      setModalAlertaMensagem(
        "Não é possível incluir esse chat, pois ele está em andamento com outro usuário. Por favor, escolha outro chat."
      );
    }
  };

  const onCriarChat = async (formData) => {
    const { contato } = formData;

    if (!contato?.nome || !contato?.whatsapp) {
      enqueueSnackbar("Preencha o nome e o telefone do contato.", { variant: "error" });
      return;
    }

    if (!cliente?._id) {
      enqueueSnackbar("Cliente não encontrado.", { variant: "error" });
      return;
    }

    const clienteIdCanal = selectedClienteId || cliente._id;

    if (isMultisession && !selectedClienteId) {
      enqueueSnackbar("Selecione o canal de envio.", { variant: "error" });
      return;
    }

    setLoadingCriar(true);
    try {
      const numero = validarNumeroWhatsapp(contato.whatsapp);

      // Se o chat já existir, apenas seleciona (respeitando as regras de inclusão)
      const chatExistente = await getActionChatInfo(clienteIdCanal, numero);
      if (chatExistente?.data) {
        validarInclusaoChatLead(chatExistente.data);
        return;
      }

      // Caso contrário, cria o chat e busca o registro completo para vincular
      await getActionCriarChatAndNovaMensagem({
        nomeCliente: contato.nome,
        numeroCliente: numero,
        statusRoom: "Atendendo",
        usuarioId: user?.id,
        clienteId: clienteIdCanal,
      });

      const novoChat = await getActionChatInfo(clienteIdCanal, numero);
      if (novoChat?.data) {
        // A mensagem de sucesso é exibida pelo fluxo de vinculação (onSelectChat),
        // somente após o vínculo ser confirmado.
        await onSelectChat(novoChat.data);
      } else {
        enqueueSnackbar("Chat criado, mas não foi possível carregá-lo. Tente pesquisar novamente.", {
          variant: "warning",
        });
      }
    } catch (error) {
      console.error("Erro ao criar chat:", error);
      enqueueSnackbar("Erro ao criar o chat. Tente novamente.", { variant: "error" });
    } finally {
      setLoadingCriar(false);
    }
  };

  const handlePesquisaChange = (e) => {
    const value = e.target.value;
    setPesquisa(value);
    
    // Limpar timeout anterior
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Limpar resultados se muito curto
    if (value.length < 3) {
      setListaContatos([]);
      return;
    }
    
    // Debounce de 500ms
    timeoutRef.current = setTimeout(() => {
      pesquisarChat(value);
    }, 500);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="w-[400px]">
          <DialogTitle>{modoCadastro ? "Cadastrar novo chat" : "Escolher chat"}</DialogTitle>

          {modoCadastro ? (
            <div className="space-y-3">
              {isMultisession && (
                <div className="space-y-1.5">
                  <Label>
                    Canal de envio <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={selectedClienteId?.toString()}
                    onValueChange={(value) => setSelectedClienteId(value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Selecione o canal" />
                    </SelectTrigger>
                    <SelectContent>
                      {listaClientesCanais.map((option) => (
                        <SelectItem key={option.cliente_id} value={option.cliente_id?.toString()}>
                          {option.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <NovoChatForm
                showSignupButton={true}
                loadingNovoContato={loadingCriar}
                callbackAddContact={onCriarChat}
              />
              <Button
                variant="ghost"
                className="w-full text-muted-foreground"
                onClick={() => setModoCadastro(false)}
                disabled={loadingCriar}
              >
                <Iconify icon="eva:arrow-back-outline" className="mr-1.5 h-4 w-4" />
                Voltar para a pesquisa
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <div>
                <Label htmlFor="search">Pesquisar WhatsApp</Label>
                <Input
                  id="search"
                  placeholder="Digite pelo menos 3 caracteres para pesquisar"
                  value={pesquisa}
                  onChange={handlePesquisaChange}
                  disabled={isLoading}
                  className="flex-1"
                />
              </div>

              <ScrollArea className="h-40 border rounded-md">
                {isLoading ? (
                  <p className="text-sm text-muted-foreground p-2 text-center">
                    Pesquisando...
                  </p>
                ) : listaContatos.length > 0 ? (
                  listaContatos.map((contato) => (
                    <Button
                      key={contato._id}
                      variant="ghost"
                      className="w-full text-left justify-start p-2 h-auto"
                      onClick={() => validarInclusaoChatLead(contato)}
                    >
                      <div className="flex flex-col items-start">
                        <span className="font-medium">
                          {formataNumeroCelular(contato.numeroCliente)}
                        </span>
                        {contato.statusRoom && (
                          <span className="text-xs text-muted-foreground">
                            Status: {contato.statusRoom}
                          </span>
                        )}
                      </div>
                    </Button>
                  ))
                ) : pesquisa.length >= 3 ? (
                  <div className="flex flex-col items-center gap-2 p-4 text-center">
                    <p className="text-sm text-muted-foreground">
                      Nenhum resultado encontrado para "{pesquisa}"
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground p-2 text-center">
                    Digite pelo menos 3 caracteres para começar a pesquisar
                  </p>
                )}
              </ScrollArea>

              <Button
                variant="outline"
                className="w-full border-dashed text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                onClick={() => setModoCadastro(true)}
              >
                <Iconify icon="mdi:chat-plus-outline" className="mr-1.5 h-4 w-4" />
                Cadastrar novo chat
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={modalAlerta} onOpenChange={() => setModalAlerta(false)}>
        <DialogContent>
          <DialogTitle>Alerta</DialogTitle>
          <p className="text-sm text-muted-foreground">{modalAlertaMensagem}</p>
          <Button
            className="mt-4"
            onClick={() => {
              setModalAlerta(false);
              setModalAlertaMensagem("");
            }}
          >
            Fechar
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MenuChannelChat;
